import { useState, useEffect, useMemo } from 'react';
import AddressFormatter from '@shopify/address';

const useAddressFormatter = (
  address,
  isBulk,
  locale = window.Shopify?.locale
) => {

  /* Bulk system requires columns to be in particular order */
  const bulkOrder = [
    ['firstName'],
    ['lastName'],
    ['address1'],
    ['address2'],
    ['city'],
    ['country'],
    ['province'],
    ['zip'],
    ['phone']
  ];

  /* Default is the order for Canada, USA, etc. according to AddressFormatter */
  const defaultOrder = [
    ['country'],
    ['firstName', 'lastName'],
    ['company'],
    ['address1'],
    ['address2'],
    ['city', 'province', 'zip'],
    ['phone']
  ];

  if (address == null) {
    address = {
      address1: "",
      address2: "",
      city: "",
      country: "",
      firstName: "",
      lastName: "",
      phone: "",
      province: "",
      zip: "",
    };
  }

  if (locale == null) {
    locale = "en-Us";
  }

  const addressFormatter = new AddressFormatter(locale);

  const [fieldOrder, setFieldOrder] = useState((isBulk) ? bulkOrder : defaultOrder);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(null);
  const [province, setProvince] = useState(null);
  const [formattedAddress, setFormattedAddress] = useState(null);

  /* 1) Set countries in the current locale */
  useEffect(() => {

    if (!addressFormatter) return;

    addressFormatter
    .getCountries()
    .then((res) => {
      setCountries(res);
    })
    .catch((error) => console.warn(error))
    ;

  }, [addressFormatter]);

  /* 2) Set selected country based on provided address country name */
  useEffect(() => {

    if (!countries.length || !address.country) return;

    const selectedCountry = countries.find((c) => c.name === address.country);
    setCountry((selectedCountry) ? selectedCountry : null);
  }, [countries, address.country]);

  /* 3) Set selected province based on provided address province name */
  useEffect(() => {
    if (!country) return;

    // Handle selected country has no provinces
    if (address.province === "") {
      setProvince(null);
      return;
    }

    const selectedProvince = country.zones.find((zone) => zone.name === address.province);
    setProvince((selectedProvince) ? selectedProvince : null);
  }, [country, address.province]);

  /* 4) Update the fieldOrder when the selected country changes */
  useEffect(() => {
    if (!country || !addressFormatter || isBulk) return;

    addressFormatter.getOrderedFields(country.code)
      .then((res) => setFieldOrder(res))
      .catch((err) => {
        console.warn(`An error occured formatting address for locale '${country.code}', using default format. `, err);
        setFieldOrder(defaultOrder);
      });

  }, [country]);

  /* One layer version of fieldOrder for vertically stacked forms */
  const stackedFieldOrder = useMemo(() => fieldOrder.flat(), [fieldOrder]);

  /* 5) Update formatted address on address change */
  useEffect(() => {
    if (!address || !addressFormatter || !country) return;

    // Ensure that useEffect (2) and (3) have finished
    // if (address.country !== country.name || address.province !== province.name) return;

    const codedAddress = {
      ...address,
      country: country.code,
      province: (province) ? province.code : ""
    };

    addressFormatter
    .format(codedAddress)
    .then((res) => {
      const formatted = (Array.isArray(res)) ? res.filter(Boolean).join("\n") : null;
      setFormattedAddress(formatted);
    })
    .catch((error) => console.warn(error))
    ;

  }, [addressFormatter, country, province, address]);

  return {
    formattedAddress,
    fieldOrder,
    stackedFieldOrder,
  };
};

export default useAddressFormatter;
